import type {
  FolderAnalysisEvidence,
  FolderAnalysisProgress,
  FolderAnalysisStatus,
  ProcessResult,
} from '../shared/desktop-api';

/** A setup sample this page is reading or has read for one workspace job (folder-analysis.ts). */
export interface Analysis {
  state: FolderAnalysisStatus['state'];
  startedAt: string;
  totalFileCount: number;
  analyzedFileCount: number;
  result: ProcessResult | null;
  failure: string | null;
  /** Bumped by every amendment the reviewer makes, so a reload can tell one snapshot from the next. */
  evidenceRevision: number;
  /** Where the reading has got to, the last the host said (a returning screen asks for it). */
  progress: FolderAnalysisProgress | null;
  /** Ends the screen's wait as a stop (Stop setup); set while a reading this tab started is running. */
  stop?: () => void;
  /** Stop setup was chosen: the run's own answer, when it comes, does not undo it. */
  stopped?: boolean;
  /** The operation a recoverable setup is offered under, the same for the page's lifetime (setup-resume.ts). */
  operationId?: string;
}

/** One analysis per workspace job, for the page's lifetime. */
export const analyses = new Map<string, Analysis>();

/** The status with the proposal and the latest progress: what a returning screen rebuilds from. */
export const evidenceOf = (jobId: string): FolderAnalysisEvidence => ({
  ...statusOf(jobId),
  result: analyses.get(jobId)?.result ?? null,
  previousResult: null,
  progress: analyses.get(jobId)?.progress ?? null,
});

/** The desktop's status shape for a job with no analysis: idle, nothing counted. */
const idle: FolderAnalysisStatus = {
  available: false,
  running: false,
  state: 'idle',
  autoAcceptEligible: false,
  resumeAvailable: false,
  canIncludeRemaining: false,
  operationId: null,
  startedAt: null,
  evidenceRevision: 0,
  failure: null,
  totalFileCount: 0,
  analyzedFileCount: 0,
  deeplyAnalyzedDocumentCount: 0,
  remainingFileCount: 0,
};

export function statusOf(jobId: string): FolderAnalysisStatus {
  const analysis = analyses.get(jobId);
  if (!analysis) return idle;
  return {
    ...idle,
    available: analysis.state === 'ready',
    running: analysis.state === 'running',
    state: analysis.state,
    // A ready proposal is used as it stands, as CapturePoint's does: the App saves it and opens
    // Documents (`false` left setups in the types editor with "Unsaved changes", 2026-09-30).
    autoAcceptEligible: analysis.state === 'ready',
    // A setup the host can run again from the documents it holds is offered as Resume (setup-resume.ts).
    resumeAvailable: analysis.state === 'recoverable',
    operationId: analysis.operationId ?? `${jobId}:${analysis.startedAt}`,
    startedAt: analysis.startedAt,
    evidenceRevision: analysis.state === 'ready' ? analysis.evidenceRevision + 1 : 0,
    failure: analysis.failure,
    totalFileCount: analysis.totalFileCount,
    analyzedFileCount: analysis.analyzedFileCount,
    deeplyAnalyzedDocumentCount: analysis.result?.documents.length ?? 0,
  };
}
